import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import CampaignImage from '~/components/CampaignImage'
import { isAcceptingEntries } from '~/lib/feature/isAcceptingEntries'
import NextLink from 'next/link'
import { OfferCount } from '../ApprovedCampaignCard/OfferCount'
import { StatusChipGroup } from '../ApprovedCampaignCard/StatusChipGroup'
import { decodeId } from '~/lib/convertId'
import {
  CampaignCostHandlingMethod,
  CampaignScheduleType,
  CampaignSnsRequirementFragment,
  CampaignStatus,
  ImageUrl,
  OfferStateCountFragment,
  RegularlyPublishedCampaignStatus,
} from '~/generated/graphql'
import { isFinish, isDenial } from '~/lib/CampaignStatus'
import { CampaignInfo } from '../CampaignInfo'
import { TabState } from '../ResponsiveCampaignsTab'
import { trimText } from '~/features/campaign_create/input_form/TagInput'
import { DateTime } from 'luxon'
import { FC, ReactNode, useCallback, useState } from 'react'
import { RegularlyPublishedOnContent } from '../regularlyPublish/RegularlyPublishedOnContent'
import { type BurdenRate, toBurdenRate } from '~/features/campaign_input/constants/burdenRate'

export type ApprovedCampaign = {
  id: string
  name: string
  status: CampaignStatus
  offerStarted?: string | null
  offerEnded?: string | null
  coverImageUrls: readonly ImageUrl[]
  gross?: number | null
  location?: {
    stationName?: string | null
  } | null
  scheduleType?: CampaignScheduleType | null
  campaignSnsRequirements: readonly CampaignSnsRequirementFragment[]
  offerStateCount: OfferStateCountFragment
  regularlyPublishedCampaigns?: {
    status: RegularlyPublishedCampaignStatus
    executionDay?: number | null
    nextExecutionDate?: string | null
  } | null
  costHandlingMethod?: CampaignCostHandlingMethod | null
  influencerBurdenRate: BurdenRate
}

type Props = {
  campaign: ApprovedCampaign
  tabState: TabState
  refetchRegularlyPublishedCampaigns: () => void
}

const useApprovedCampaignCard = (campaign: ApprovedCampaign) => {
  // チップの状態
  const chipsStatus = {
    isAcceptingEntries:
      !!campaign.offerStarted &&
      !!campaign.offerEnded &&
      isAcceptingEntries(campaign.offerStarted, campaign.offerEnded),
    hasOfferInProgress: campaign.offerStateCount.working > 0,
    isFinished: isFinish(campaign.status),
    isDenial: isDenial(campaign.status),
    costHandlingMethod: campaign.costHandlingMethod,
    influencerBurdenRate: campaign.influencerBurdenRate,
  }

  const campaignInfo = {
    campaignName: campaign.name,
    campaignSnsRequirements: campaign.campaignSnsRequirements,
    gross: campaign.gross,
    nearestStation: campaign.location?.stationName,
    offerStarted: campaign.offerStarted,
    offerEnded: campaign.offerEnded,
  } as const

  const campaignId = decodeId(campaign.id).id
  return {
    campaignId,
    href: `/campaigns/${campaignId}/offers`,
    coverImage: campaign.coverImageUrls[0],
    campaignName: campaign.name,
    chipsStatus,
    campaignInfo,
    offerCount: campaign.offerStateCount,
  } as const
}

export const ResponsiveApprovedCampaignCard: React.FC<Props> = ({
  campaign,
  tabState,
  refetchRegularlyPublishedCampaigns,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
  const handleOpenDialog = (campaignId: string) => {
    setSelectedCampaignId(campaignId)
  }

  const handleClose = useCallback(() => {
    refetchRegularlyPublishedCampaigns()
    setSelectedCampaignId(null)
  }, [refetchRegularlyPublishedCampaigns])

  const used = useApprovedCampaignCard(campaign)
  if (!used) return null // 不整合なデータのケース
  const { href, coverImage, chipsStatus, campaignInfo, offerCount } = used

  const executionDay = campaign.regularlyPublishedCampaigns?.executionDay
  const nextExecutionDate = campaign.regularlyPublishedCampaigns?.nextExecutionDate
  const isRegularPublishEnabled =
    campaign.regularlyPublishedCampaigns?.status === RegularlyPublishedCampaignStatus.On &&
    executionDay !== undefined &&
    executionDay !== null

  return (
    <>
      <Card style={{ height: '100%' }}>
        <LinkWrapper href={href} tabState={tabState}>
          <CardActionArea
            component={tabState === 'regularPublished' ? 'button' : 'a'}
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              justifyContent: 'flex-start',
              alignItems: isMobile ? 'flex-start' : 'stretch',
              gap: isMobile ? 16 : 0,
              padding: isMobile ? 16 : undefined,
              height: '100%',
            }}
            onClick={tabState === 'regularPublished' ? () => handleOpenDialog(campaign.id) : undefined}
          >
            <Box
              sx={{
                position: 'relative',
                flex: isMobile ? '0 0 115px' : undefined,
                '&::before': {
                  content: "''",
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 100,
                },
              }}
            >
              <CampaignImage
                image={{
                  url: coverImage?.original ?? '',
                  w480xh480: coverImage && { url: coverImage.medium },
                  w120xh120: coverImage && { url: coverImage.small },
                }}
                variant={isRegularPublishEnabled ? 'primary' : 'noBorder'}
              />

              {isRegularPublishEnabled && (
                <Typography
                  sx={{
                    backgroundColor: 'primary.main',
                  }}
                  position="absolute"
                  top={isMobile ? undefined : 0}
                  bottom={isMobile ? 0 : undefined}
                  left={isMobile ? 0 : undefined}
                  right={0}
                  zIndex={101}
                  color="text.invertPrimary"
                  fontWeight="bold"
                  fontSize={10}
                  lineHeight={1.5}
                  paddingX={isMobile ? 0 : 2}
                  paddingY={isMobile ? 0 : 1}
                  textAlign={isMobile ? 'center' : 'left'}
                  borderRadius={isMobile ? '0 0 8px 8px' : undefined}
                  boxShadow={isMobile ? undefined : '0px 4px 4px rgba(0, 0, 0, 0.15)'}
                >
                  {tabState === 'regularPublished' && nextExecutionDate
                    ? trimText(`実行日：${DateTime.fromISO(nextExecutionDate).toFormat('yyyy/MM/dd')}`)
                    : '定期公開中'}
                </Typography>
              )}
            </Box>
            <CardContent sx={{ p: isMobile ? 0 : undefined, overflowX: 'scroll', scrollbarWidth: 'none' }}>
              <Stack gap={1}>
                <StatusChipGroup {...chipsStatus} />
                <Box height={1} />
                <CampaignInfo
                  {...campaignInfo}
                  campaignNameStyle={tabState === 'regularPublished' ? 'lineBreak' : 'ellipsis'}
                />
                {tabState !== 'regularPublished' && (
                  <>
                    <Divider />
                    <OfferCount {...offerCount} />
                  </>
                )}
              </Stack>
            </CardContent>
          </CardActionArea>
        </LinkWrapper>
      </Card>
      <RegularlyPublishedCampaignSettingDialog
        {...{
          campaignId: selectedCampaignId,
          executionDay,
          handleClose,
        }}
      />
    </>
  )
}

const LinkWrapper = ({ children, tabState, href }: { children: ReactNode; tabState: TabState; href: string }) => {
  if (tabState === 'regularPublished') {
    return children
  }

  return (
    <NextLink href={href} passHref legacyBehavior>
      {children}
    </NextLink>
  )
}

type RegularlyPublishedCampaignSettingDialogProps = {
  campaignId: string | null
  executionDay: number | null | undefined
  handleClose: VoidFunction
}

const RegularlyPublishedCampaignSettingDialog: FC<RegularlyPublishedCampaignSettingDialogProps> = (props) => {
  const { campaignId, executionDay, handleClose } = props
  const showDialog = !!campaignId
  return (
    <Dialog open={showDialog} onClose={handleClose}>
      <DialogTitle>定期公開の設定</DialogTitle>
      {campaignId && (
        <RegularlyPublishedOnContent onClose={handleClose} campaignId={campaignId} executionDay={executionDay} />
      )}
    </Dialog>
  )
}
